import { Log } from "../log";

export interface Dictionary<T> {
    [Key: string]: T;
}
export class Mutex {
    
    private mutex = Promise.resolve();

    lock(): PromiseLike<() => void> {

      let begin: (unlock: () => void) => void = unlock => {};

      this.mutex = this.mutex.then(() => {
        return new Promise(begin);
      });
      
      return new Promise(res => {
        begin = res;
      });
    }

    async dispatch<T>(fn: (() => T) | (() => PromiseLike<T>)): Promise<T> {
      const unlock = await this.lock();
      try {
        return await Promise.resolve(fn());
      } finally {
        unlock();
      }
    }
}

export class Utilities {
    static stringEmpty(value?: string) {
        return (value == null || value.trim().length === 0);
    }

    static isValidEmail(email: string) {
        // tslint:disable-next-line:max-line-length
        const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    static async resizeImage(imageFile: Blob, maxWidth: number, maxHeight: number, orientation: number = 1) {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = document.createElement('img');

            if (ctx == null) {
                reject('Could not get canvas context');
                return;
            }

            img.onload = (data => {
                const iw = img.width;
                const ih = img.height;
                const maxW = Math.min(maxWidth, iw);
                const maxH = Math.min(maxHeight, ih);
                const scale = Math.min((maxW / iw), (maxH / ih));
                const iwScaled = iw * scale;
                const ihScaled = ih * scale;
                canvas.width = iwScaled;
                canvas.height = ihScaled;

                // set proper canvas dimensions before transform & export
                if (4 < orientation && orientation < 9) {
                    canvas.width = ihScaled;
                    canvas.height = iwScaled;
                } else {
                    canvas.width = iwScaled;
                    canvas.height = ihScaled;
                }

                // transform context before drawing image
                switch (orientation) {
                    case 2:
                        // horizontal flip
                        ctx.translate(iwScaled, 0);
                        ctx.scale(-1, 1);
                        break;
                    case 3:
                        // 180° rotate left
                        ctx.translate(iwScaled, ihScaled);
                        ctx.rotate(Math.PI);
                        break;
                    case 4:
                        // vertical flip
                        ctx.translate(0, ihScaled);
                        ctx.scale(1, -1);
                        break;
                    case 5:
                        // vertical flip + 90 rotate right
                        ctx.rotate(0.5 * Math.PI);
                        ctx.scale(1, -1);
                        break;
                    case 6:
                        // 90° rotate right
                        ctx.rotate(0.5 * Math.PI);
                        ctx.translate(0, -ihScaled);
                        break;
                    case 7:
                        // horizontal flip + 90 rotate right
                        ctx.rotate(0.5 * Math.PI);
                        ctx.translate(iwScaled, -ihScaled);
                        ctx.scale(-1, 1);
                        break;
                    case 8:
                        // 90° rotate left
                        ctx.rotate(-0.5 * Math.PI);
                        ctx.translate(-iwScaled, 0);
                        break;
                    default: // 1 do nothing
                        break;
                }

                ctx.drawImage(img, 0, 0, iwScaled, ihScaled);
                canvas.toBlob(blob => {
                    if (blob == null) {
                        reject('Could not convert canvas to blob');
                        return;
                    }
                    resolve(URL.createObjectURL(blob));
                }, imageFile.type, 0.75);
            });

            img.onerror = (error => {
                Log.debug('Error resizing image', error);
                reject(error);
            });

            img.src = URL.createObjectURL(imageFile);
        });
    }

    static splitCamelCaseToString(camelCaseString) {
        // Split the string at each point before an uppercase letter and join with a space
        return camelCaseString
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
    }

    static base64ToBlob(b64: string) {
        // convert base64 to raw binary data held in a string
        const byteString = atob(b64.split(',')[1]);

        // separate out the mime component
        const mimeString = b64.split(',')[0].split(':')[1].split(';')[0];

        // write the bytes of the string to an ArrayBuffer
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        // write the ArrayBuffer to a blob, and you're done
        const bb = new Blob([ab], { type: mimeString });
        return bb;
    }

    static getBase64FromDataUrl(dataUrl: string) {
        return new Promise<string>((resolve, reject) => {
            fetch(dataUrl, { mode: 'cors' }).then(r => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve(reader.result as string);
                };
                r.blob().then(blob => {
                    reader.readAsDataURL(blob);
                });
            });
        });
    }

    static downloadBlob(file: Blob, saveAs: string) {
        // Generate a download link using the blob as the data
        const link = document.createElement("a");
        link.href = URL.createObjectURL(file);
        link.setAttribute('visibility', 'hidden');
        link.download = saveAs;

        // Add the link to the dom, click it to trigger dl, then remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    static downloadFromBase64(base64: string, saveAs: string) {
        // Convert the base64 to a blob file, then run the download blob function
        const blob = this.base64ToBlob(base64);
        this.downloadBlob(blob, saveAs);
    }

    static generateUuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
    
    static getRandomString(length: number) {
        let text = '';
        const possible = 'ABCDEFGHIJKLMNBBOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890';
        for (let i = 0; i < length; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }
        return text;
    }
    
    static getRandomNumber(length: number) {
        let text = '';
        const possible = '0123456789';
        for (let i = 0; i < length; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }
        return +text;
    }
    
    static async wait(ms) {
        return new Promise(resolve => {
            setTimeout(resolve, ms);
        });
    }

    static assignOnlyExisting(target: any, source: any) {
        Object.keys(target).forEach(key => {
            if (source.hasOwnProperty(key)) {
                target[key] = source[key];
            }
        });
        return target;
    }
}
